import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Text } from '@/ui/design-system/components';
import { widgetQuery } from '@/utils/hooks/reports';
import ReportWidget from './ReportWidget';
import { isExecutableArgs } from './widgetCatalog';

// Debounce the draft args so toggling controls / typing doesn't fire a widget
// POST per change. draftArgs is a fresh object on each edit, so the effect resets
// the timer each change — classic debounce.
const useDebouncedValue = (value, delay) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

function PreviewShell({ children }) {
    return (
        <div className="min-h-[160px] flex items-center justify-center rounded-xl border border-border bg-background-low-em p-4 text-center">
            {children}
        </div>
    );
}

// Live preview of the draft widget config: runs the same widgetQuery the report
// grid uses (so it warms the shared cache) and renders the matching inline
// visual. No client-side validation — an invalid-but-complete config fetches and
// shows ReportWidget's error tile, which IS the validation.
function WidgetPreview({ args, title, onToggleAggregate }) {
    const debounced = useDebouncedValue(args, 400);
    const enabled = isExecutableArgs(debounced);
    const q = useQuery({ ...widgetQuery(debounced), enabled });

    if (!enabled) {
        return (
            <PreviewShell>
                <Text size="sm" color="muted">
                    Pick a widget type and an entity to preview.
                </Text>
            </PreviewShell>
        );
    }

    return (
        <ReportWidget
            args={debounced}
            data={q.data}
            isLoading={q.isLoading}
            isError={q.isError}
            error={q.error}
            title={title}
            onToggleAggregate={onToggleAggregate}
        />
    );
}

export default WidgetPreview;
